import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { endpoints } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet } from "lucide-react";

export default function UploadDataset() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("");
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const submit = async () => {
    if (!file) { toast.error("Please add a dataset file"); return; }
    if (!name.trim()) { toast.error("Give your project a name"); return; }
    setBusy(true);
    try {
      const { data: project } = await endpoints.createProject({
        name: name.trim(), description: "", goal_prompt: goal,
      });
      await endpoints.uploadDataset(project.id, file, goal);
      await endpoints.startTraining(project.id, null);
      toast.success("Pipeline started");
      nav(`/projects/${project.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <main className="flex-1 max-w-[1000px] mx-auto w-full px-8 py-10">
        <div className="mb-8 border-b border-border pb-6">
          <div className="label-mono mb-2">// NEW RUN</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">Upload a dataset</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            The agent will profile, plan and train models automatically. Add a short goal to steer it.
          </p>
        </div>

        <div className="space-y-6">
          <div>
            <Label className="label-mono">Project name</Label>
            <Input
              data-testid="upload-project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Customer churn Q1"
              className="mt-2 bg-background"
            />
          </div>

          <div>
            <Label className="label-mono">Goal prompt (optional)</Label>
            <Textarea
              data-testid="upload-goal"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="Predict churn, ignore customer_id, optimise recall"
              rows={3}
              className="mt-2 bg-background"
            />
          </div>

          <div>
            <Label className="label-mono">Dataset file</Label>
            <div
              data-testid="upload-drop-zone"
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
              className={`mt-2 border-2 border-dashed rounded-sm p-12 text-center transition-colors ${
                drag ? "border-accent bg-accent/5" : "border-border bg-card"
              }`}
            >
              {!file ? (
                <>
                  <UploadCloud className="w-8 h-8 mx-auto text-muted-foreground mb-3" strokeWidth={1.5} />
                  <div className="font-display font-semibold text-lg">Drop CSV, Excel or Parquet</div>
                  <p className="text-xs text-muted-foreground mt-1">or</p>
                  <label className="inline-flex mt-3">
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls,.parquet"
                      onChange={(e) => setFile(e.target.files?.[0])}
                      className="hidden"
                      data-testid="upload-file-input"
                    />
                    <span className="cursor-pointer text-sm bg-accent text-accent-foreground px-4 py-2 rounded-sm hover:bg-accent/90 transition-colors">
                      Browse files
                    </span>
                  </label>
                </>
              ) : (
                <div className="flex items-center gap-3 justify-center">
                  <FileSpreadsheet className="w-6 h-6 text-accent" strokeWidth={1.5} />
                  <div className="text-left">
                    <div className="font-mono text-sm">{file.name}</div>
                    <div className="label-mono">{(file.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <button
                    onClick={() => setFile(null)}
                    className="text-xs text-muted-foreground hover:text-destructive ml-4"
                    data-testid="upload-remove-file"
                  >
                    remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-border">
            <Button
              onClick={submit}
              disabled={busy}
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm px-6 h-11"
              data-testid="upload-submit"
            >
              {busy ? "Starting…" : "Start automated pipeline"}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
